import { Action, Subject } from "@aitisal/contracts/common";
import { AppShell, NavLink, Stack, Tooltip } from "@mantine/core";
import {
  IconClipboardList,
  IconClipboardListFilled,
  IconLayoutBottombarExpand,
  IconLayoutBottombarExpandFilled,
  IconLayoutDistributeHorizontal,
  IconLayoutDistributeHorizontalFilled,
  IconLayoutGrid,
  IconLayoutGridFilled,
  IconPresentationAnalytics,
  IconPresentationAnalyticsFilled,
  IconReceipt,
  IconReceiptFilled,
  IconSettings,
  IconSettingsFilled,
  IconTable,
  IconTableFilled,
  IconUsersGroup,
} from "@tabler/icons-react";
import { Link, useLocation } from "@tanstack/react-router";
import { useAbilityStore } from "../../stores/rolesRightsStore";
import { useAppShellLayout } from "../../stores/stores";

interface SidebarProps {
  isMobile: boolean;
  opened: boolean;
  onClose: () => void;
  onToggle: () => void;
}

export function Sidebar({ isMobile, opened, onClose, onToggle }: SidebarProps) {
  const location = useLocation();
  const { ability } = useAbilityStore();
  const { isSidebarCollapsed } = useAppShellLayout();

  const navigationItems = [
    {
      label: "Home",
      iconActive: IconLayoutGrid,
      iconInactive: IconLayoutGridFilled,
      href: "/",
      isAble: ability && ability?.can(Action?.Read, Subject?.Report),
    },
    {
      label: "Menu",
      iconActive: IconLayoutBottombarExpand,
      iconInactive: IconLayoutBottombarExpandFilled,
      href: "/menu",
      isAble: ability && ability?.can(Action?.Read, Subject?.Menu),
    },
    {
      label: "Tables Management",
      iconActive: IconTable,
      iconInactive: IconTableFilled,
      href: "/tables-management",
      isAble: ability && ability?.can(Action?.Read, Subject?.PointOfOrder),
    },
    {
      label: "Orders",
      iconActive: IconLayoutDistributeHorizontal,
      iconInactive: IconLayoutDistributeHorizontalFilled,
      href: "/orders",
      isAble: ability && ability?.can(Action?.Read, Subject?.Order),
    },
    {
      label: "Point Of Sale",
      iconActive: IconReceipt,
      iconInactive: IconReceiptFilled,
      href: "/point-of-sale",
      isAble: ability && ability?.can(Action?.Read, Subject?.POS),
    },
    {
      label: "Customers",
      iconActive: IconUsersGroup,
      iconInactive: IconUsersGroup,
      href: "/customers",
      isAble: ability && ability?.can(Action?.Read, Subject?.Customer),
    },
    {
      label: "Order History",
      iconActive: IconClipboardList,
      iconInactive: IconClipboardListFilled,
      href: "/order-history",
      isAble: ability && ability?.can(Action?.Read, Subject?.Report),
    },
    {
      label: "Analytics",
      iconActive: IconPresentationAnalytics,
      iconInactive: IconPresentationAnalyticsFilled,
      href: "/analytics",
      isAble: ability && ability?.can(Action?.Read, Subject?.Report),
    },
  ];

  const settingsItem = {
    label: "Settings",
    iconActive: IconSettings,
    iconInactive: IconSettingsFilled,
    href: "/settings",
    isAble: ability && ability?.can(Action?.Read, Subject?.Restaurant),
  };

  const handleNavClick = () => {
    if (isMobile) {
      onClose();
    }
  };

  return (
    <AppShell.Navbar
      style={{
        backgroundColor: "#EBEBEB",
        transform: opened || !isMobile ? "translateX(0)" : "translateX(-100%)",
        opacity: opened || !isMobile ? 1 : 0,
      }}
      className="border-r-0 transition-all duration-300 ease-in-out rounded-2xl rounded-b-none rounded-r-none max-w-[240px]"
    >
      {/* Navigation */}
      <AppShell.Section grow className="overflow-y-auto p-2">
        <Stack gap={1}>
          {navigationItems
            .filter((item) => item.isAble)
            .map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = isActive ? item.iconActive : item.iconInactive;

              const navLinkContent = (
                <NavLink
                  variant="filled"
                  color="#FAFAFA"
                  key={item.href}
                  component={Link}
                  to={item.href}
                  label={!isSidebarCollapsed ? item.label : undefined}
                  leftSection={<Icon size={16} />}
                  active={isActive}
                  onClick={handleNavClick}
                  className={`
                    rounded-md transition-all p-2 text-[13px] font-medium duration-300 ease-in-out
                    ${
                      isActive
                        ? "  bg-[#FAFAFA] text-black"
                        : "  text-gray-700 hover:bg-gray-50 hover:text-black"
                    }
                    ${isSidebarCollapsed ? "justify-center" : ""}
                  `}
                  style={{ color: "black" }}
                  styles={{
                    label: {
                      fontSize: "13px",
                      fontWeight: 500,
                      display: isSidebarCollapsed ? "none" : "block",
                      transition: "opacity 200ms ease-in-out",
                      opacity: isSidebarCollapsed ? 0 : 1,
                    },
                    section: {
                      marginRight: isSidebarCollapsed ? "0" : "12px",
                      transition: "margin-right 300ms ease-in-out",
                    },
                    root: {
                      justifyContent: isSidebarCollapsed
                        ? "center"
                        : "flex-start",
                      transition: "justify-content 300ms ease-in-out",
                    },
                  }}
                />
              );

              return isSidebarCollapsed ? (
                <Tooltip
                  key={item.href}
                  label={item.label}
                  position="right"
                  withArrow
                >
                  {navLinkContent}
                </Tooltip>
              ) : (
                navLinkContent
              );
            })}
        </Stack>
      </AppShell.Section>

      {/* Settings - Always at bottom */}
      <AppShell.Section className="p-2">
        {(() => {
          const isActive = location.pathname === settingsItem.href;
          const Icon = isActive
            ? settingsItem.iconActive
            : settingsItem.iconInactive;
          const isAble = settingsItem.isAble;

          const settingsNavLink = (
            <NavLink
              variant="filled"
              color="#FAFAFA"
              key={settingsItem.href}
              component={Link}
              to={settingsItem.href}
              label={!isSidebarCollapsed ? settingsItem.label : undefined}
              leftSection={<Icon size={16} />}
              active={isActive}
              onClick={handleNavClick}
              className={`
                    rounded-md transition-all p-2 text-[13px] font-medium duration-300 ease-in-out
                    ${
                      isActive
                        ? "  bg-[#FAFAFA] text-black"
                        : "  text-gray-700 hover:bg-gray-50 hover:text-black"
                    }
                    ${isSidebarCollapsed ? "justify-center" : ""}
                  `}
              style={{ color: "black", display: isAble ? "flex" : "none" }}
              styles={{
                label: {
                  fontSize: "13px",
                  fontWeight: 500,
                  display: isSidebarCollapsed ? "none" : "block",
                  transition: "opacity 200ms ease-in-out",
                  opacity: isSidebarCollapsed ? 0 : 1,
                },
                section: {
                  marginRight: isSidebarCollapsed ? "0" : "12px",
                  transition: "margin-right 300ms ease-in-out",
                },
                root: {
                  justifyContent: isSidebarCollapsed ? "center" : "flex-start",
                  transition: "justify-content 300ms ease-in-out",
                },
              }}
            />
          );

          return isSidebarCollapsed ? (
            <Tooltip label={settingsItem.label} position="right" withArrow>
              {settingsNavLink}
            </Tooltip>
          ) : (
            settingsNavLink
          );
        })()}
      </AppShell.Section>
    </AppShell.Navbar>
  );
}
